import React, { useCallback, useMemo } from "react";
import { useFormikContext } from "formik";
import { JournalEntryDrawerFormikConfig } from "@components/accounting/journalEntryDrawer/JournalEntryDrawer";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { Field } from "@design-system/Field/Field";
import { Input } from "@design-system/Input/Input";
import { getShortJournalEntryReferenceNumber } from "@common/helpers/accounting";
import { JournalEntryDocuments } from "@components/accounting/journalEntryDrawer/JournalEntryDocuments";
import { useFinancialDocumentUpload } from "@components/documents/FinancialDocumentUpload/useFinancialDocumentUpload";
import { Button } from "@design-system/Button/Button";
import { JournalEntryRecurrenceSelect } from "@components/accounting/journalEntryDrawer/JournalEntryRecurrenceSelect";
import { useEntities } from "@hooks/useEntities";
import { useOverlays } from "../../../contexts/OverlayContext";
import styles from "./JournalEntrySidebar.module.scss";

const fields = createFormikFields<JournalEntryDrawerFormikConfig>();

interface Props {
    isEditable: boolean;
    isMemoEditable: boolean;
}

export const JournalEntrySidebar: React.FC<Props> = ({
    isEditable,
    isMemoEditable,
}) => {
    const { rootProps } = useFinancialDocumentUpload();
    const formikContext = useFormikContext<JournalEntryDrawerFormikConfig>();
    const { values } = formikContext;
    const { reversedManualJournalEntryId, reversingManualJournalEntryIds } =
        values.journalEntry;
    const reverseMinDate = useMemo(() => {
        if (!values.journalEntry.date) {
            return undefined;
        }

        return values.journalEntry.date;
    }, [values.journalEntry.date]);

    const shouldDisableReversedDate =
        !!values.journalEntry.recurringRule?.period;
    const shouldDisableRecurringRule = !!values.journalEntry.reversedAt;

    const entities = useEntities({ onlyBusiness: true });
    const { openOverlay } = useOverlays();
    const handleJournalEntryClick = useCallback(
        (journalEntryId: string) => {
            const selectedEntity = entities.find(
                (entity) => entity.id === values.entityId,
            );

            if (!selectedEntity) {
                return;
            }

            openOverlay("journalEntryDrawer", {
                entity: selectedEntity,
                ledgerBasis: values.ledgerBasis,
                ledgerId: values.ledgerId,
                journalEntryId,
            });
        },
        [
            entities,
            values.ledgerBasis,
            values.ledgerId,
            values.entityId,
            openOverlay,
        ],
    );

    return (
        <div className={styles.sidebar}>
            <div className={styles.details}>
                <h6 className={styles.sectionHeader}>Details</h6>
                {values.journalEntryId && (
                    <Field label="Reference" className={styles.referenceField}>
                        <Input
                            size="xl"
                            value={getShortJournalEntryReferenceNumber(
                                values.journalEntryId,
                            )}
                            disabled
                        />
                    </Field>
                )}

                <JournalEntryRecurrenceSelect
                    disabled={!isEditable || shouldDisableRecurringRule}
                />
                {!reversedManualJournalEntryId ? (
                    <>
                        <fields.Date
                            className={styles.fieldContainer}
                            name="journalEntry.reversedAt"
                            label="Reverse date"
                            variant="panel-input"
                            clearable
                            emptyValue={undefined}
                            minDate={reverseMinDate}
                            placeholder={
                                !isEditable || shouldDisableReversedDate
                                    ? ""
                                    : "Add reversal"
                            }
                            disabled={!isEditable || shouldDisableReversedDate}
                            buttonProps={{
                                variant: "primary",
                                size: "xl",
                                block: true,
                            }}
                        />
                        {reversingManualJournalEntryIds?.map((id) => (
                            <Button
                                size="xl"
                                className={styles.linkedJournalEntryButton}
                                key={id}
                                onClick={() => handleJournalEntryClick(id)}
                                variant="primary"
                            >
                                View reversed journal entry
                            </Button>
                        ))}
                    </>
                ) : (
                    <>
                        <span>This is a reversed journal entry.</span>
                        <Button
                            className={styles.linkedJournalEntryButton}
                            onClick={() =>
                                handleJournalEntryClick(
                                    reversedManualJournalEntryId,
                                )
                            }
                            size="xl"
                            variant="primary"
                        >
                            View original journal entry
                        </Button>
                    </>
                )}

                <fields.TextArea
                    name="journalEntry.memo"
                    label="Memo"
                    className={styles.fieldContainer}
                    size="xl"
                    placeholder={isMemoEditable ? "Add memo" : ""}
                    disabled={!isMemoEditable}
                    rows={3}
                    resizable
                />
            </div>

            <div className={styles.attachments} {...rootProps}>
                <h6 className={styles.sectionHeader}>Attachments</h6>
                <JournalEntryDocuments />
            </div>
        </div>
    );
};
